/**
 * 视频生成服务 API 层
 * 统一封装各视频提供商的接口
 */

import { authenticatedFetch } from '../utils/api';
import { isDomesticOfficialSeedance, validateDomesticSeedanceRequest } from '../utils/domesticSeedanceCapabilities';
import { API_HOST } from '../utils/constants';
import {
  assertSeedance25SubmissionProvider,
  getSeedance25Capabilities,
  getSeedance25ReferenceUrl,
  getSeedance25ReferenceMediaType,
  isModelArkSeedance25Provider,
  normalizeSeedance25SubmissionProvider,
  normalizeSeedance25RatioForMode,
  normalizeSeedance25Resolution,
} from '../utils/seedance25Capabilities';
import {
  isDurableVideoPollingProvider,
  VIDEO_POLLING_MAX_MS,
} from '../utils/projectRuntime';

export { isDurableVideoPollingProvider, VIDEO_POLLING_MAX_MS } from '../utils/projectRuntime';

const isProviderAssetIdKey = (key) => {
  const normalized = String(key || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  return normalized === 'assetid'
    || normalized === 'assetsid'
    || normalized.endsWith('assetid')
    || normalized.endsWith('assetstatus')
    || normalized.endsWith('libraryid')
    || (normalized.includes('library') && /(key|token|secret|status)$/.test(normalized))
    || (normalized.includes('asset') && /(apikey|accesskey|secretkey)$/.test(normalized));
};

// Browser state can contain IDs from another provider or a previous group. Send
// source identity to our server, which verifies the current library and uses its
// Asset ID in the vendor request. This is not a direct-URL vendor submission.
const stripSeedance25AssetIds = (value) => {
  if (Array.isArray(value)) return value.map(stripSeedance25AssetIds);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !isProviderAssetIdKey(key))
      .map(([key, nestedValue]) => [key, stripSeedance25AssetIds(nestedValue)]),
  );
};

const normalizeSeedance25SourceMedia = (provider, payload) => {
  const capabilities = isDomesticOfficialSeedance(provider)
    ? { label: 'Seedance 2.0（国内官方）', requiresHttpsReferences: false, acceptsDataImage: true }
    : getSeedance25Capabilities(provider);
  const media = (item, mediaType = getSeedance25ReferenceMediaType(item)) => {
    const url = String(getSeedance25ReferenceUrl(item) || '').trim();
    if (!url || /^asset:\/\//i.test(url)) throw new Error(`${capabilities.label} 缺少原始素材 URL，请重新选择原始图片、视频或音频，不能仅使用素材库 ID`);
    const directUrl = capabilities.requiresHttpsReferences ? /^https:\/\//i : /^https?:\/\//i;
    if (!directUrl.test(url) && !(mediaType === 'image' && capabilities.acceptsDataImage && /^data:image\//i.test(url))) {
      throw new Error(`${capabilities.label} 参考素材必须使用${capabilities.requiresHttpsReferences ? ' HTTPS' : '公网'} URL；图片也可使用图片数据`);
    }
    return url;
  };
  const result = { ...payload };
  const sourceRecord = (item, field, url) => ({
    ...Object.fromEntries(Object.entries(item || {}).filter(([, value]) => typeof value !== 'string' || !/^asset:\/\//i.test(value.trim()))),
    [field]: url,
  });
  for (const key of ['startImageUrl', 'endImageUrl', 'imageUrl', 'generated_image', 'audioUrl']) {
    if (result[key]) {
      const fallback = key === 'endImageUrl' ? { originalUrl: payload.originalEndImageUrl || payload.endImageOriginalUrl }
        : key === 'audioUrl' ? { originalUrl: payload.originalAudioUrl, sourceUrl: payload.sourceAudioUrl }
          : { originalUrl: payload.originalStartImageUrl || payload.originalImageUrl || payload.originalUrl, sourceUrl: payload.sourceImageUrl || payload.sourceUrl, imageUrl: payload.imageUrl };
      result[key] = media(typeof result[key] === 'object' ? result[key] : { ...fallback, url: result[key] }, key === 'audioUrl' ? 'audio' : 'image');
    }
  }
  if (Array.isArray(result.referenceUrls)) result.referenceUrls = result.referenceUrls.map((item) => media(item));
  if (Array.isArray(result.guaVideoContents)) result.guaVideoContents = result.guaVideoContents.map((item) => (
    item?.type === 'text' ? item : { type: item?.type, role: item?.role, url: media(item) }
  ));
  for (const key of ['refCharacterImages', 'refSceneImages', 'refPropImages']) {
    if (Array.isArray(result[key])) result[key] = result[key].map((item) => sourceRecord(item, 'base64', media(item, 'image')));
  }
  for (const key of ['referenceImages', 'storyboardVideoInputPackage']) {
    if (Array.isArray(result[key])) result[key] = result[key].map((item) => (
      typeof item === 'string' ? media(item) : sourceRecord(item, 'url', media(item))
    ));
  }
  return result;
};

const createVideoRequestId = () => {
  if (globalThis.crypto?.randomUUID) return `video-${globalThis.crypto.randomUUID()}`;
  return `video-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
};

const WANX_SUBMISSION_ID_CACHE_PREFIX = 'wanx_video_submission_request_id_v1:';
// Wan3 的原始网络歧义最多保留 24 小时，并复用同一 requestId，避免一次点击
// 被重放成多笔计费任务。只有后端明确返回 submission_unknown 后，才允许下一次
// 用户点击创建新的 requestId。
const WANX_SUBMISSION_ID_TTL_MS = 24 * 60 * 60 * 1000;
const wanxSubmissionRequestIds = new Map();
const wanxSubmissionsInFlight = new Map();

const WANX_SUBMISSION_COORDINATOR = Object.freeze({
  cachePrefix: WANX_SUBMISSION_ID_CACHE_PREFIX,
  ttlMs: WANX_SUBMISSION_ID_TTL_MS,
  requestIds: wanxSubmissionRequestIds,
  inFlight: wanxSubmissionsInFlight,
  forwardClientSubmissionScope: false,
  // Wan3 allows a fresh, user-triggered draw after the backend has explicitly confirmed that
  // the previous create outcome is unknown. Raw browser/network ambiguity still keeps the old
  // requestId until the backend gives that confirmation.
  releaseRequestIdOnConfirmedUnknown: true,
  // A repeated, already-reserved submission_unknown request is rejected before the provider is
  // contacted. The backend marks only that branch as safe for one in-click ID rollover.
  retryKnownPreviousUnknownWithFreshRequest: true,
});

// Wand Vega 的 VideoAgent 专用请求也会产生费用；网络结果未知时复用同一个
// requestId，绝不因为再次点击而自动创建第二笔上游任务。
const WAND_SUBMISSION_COORDINATOR = Object.freeze({
  cachePrefix: 'wand_vega_video_submission_request_id_v1:',
  ttlMs: WANX_SUBMISSION_ID_TTL_MS,
  requestIds: new Map(),
  inFlight: new Map(),
});

const isWanxSubmissionProvider = (provider) => (
  ['wanx', 'wan3.0-video', 'wan3.0-video-prime']
    .includes(String(provider || '').trim().toLowerCase())
);

const getSubmissionCoordinator = (provider) => {
  return isWanxSubmissionProvider(provider) ? WANX_SUBMISSION_COORDINATOR : null;
};

const stableJsonValue = (value) => {
  if (Array.isArray(value)) return value.map(stableJsonValue);
  if (!value || typeof value !== 'object') return value;
  if (typeof value.toJSON === 'function') return stableJsonValue(value.toJSON());

  return Object.keys(value)
    .sort()
    .reduce((target, key) => {
      const nestedValue = value[key];
      if (nestedValue !== undefined && typeof nestedValue !== 'function' && typeof nestedValue !== 'symbol') {
        target[key] = stableJsonValue(nestedValue);
      }
      return target;
    }, {});
};

const stableJsonStringify = (value) => JSON.stringify(stableJsonValue(value));

// 仅用于当前页面的单飞索引；请求正文不会进入 Map 或浏览器存储。
const shortVolatileFingerprint = (text) => {
  let h1 = 0xdeadbeef ^ text.length;
  let h2 = 0x41c6ce57 ^ text.length;
  let h3 = 0x9e3779b9 ^ text.length;
  let h4 = 0x85ebca6b ^ text.length;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
    h3 = Math.imul(h3 ^ code, 2246822507);
    h4 = Math.imul(h4 ^ code, 3266489909);
  }
  const mix = (value, other) => (
    Math.imul(value ^ (value >>> 16), 2246822507)
    ^ Math.imul(other ^ (other >>> 13), 3266489909)
  ) >>> 0;
  return [mix(h1, h2), mix(h2, h3), mix(h3, h4), mix(h4, h1)]
    .map(value => value.toString(16).padStart(8, '0'))
    .join('');
};

const createPersistentFingerprint = async (serializedRequest) => {
  try {
    if (globalThis.crypto?.subtle && typeof TextEncoder !== 'undefined') {
      const digest = await globalThis.crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(serializedRequest),
      );
      return Array.from(new Uint8Array(digest))
        .slice(0, 16)
        .map(value => value.toString(16).padStart(2, '0'))
        .join('');
    }
  } catch {
    // 某些非安全上下文无法调用 Web Crypto；仍只保存短指纹，不回退保存请求正文。
  }
  return shortVolatileFingerprint(serializedRequest);
};

const getVideoSubmissionUserScope = () => {
  try {
    const userId = globalThis.localStorage?.getItem('app_user_id');
    if (userId) return String(userId);
    const user = JSON.parse(globalThis.localStorage?.getItem('app_user') || 'null');
    return String(user?.name || user?.username || 'default_user');
  } catch {
    return 'default_user';
  }
};

const getSessionStorage = () => {
  try {
    return globalThis.sessionStorage || null;
  } catch {
    return null;
  }
};

const isUsableCachedRequestId = (record, now = Date.now(), ttlMs = WANX_SUBMISSION_ID_TTL_MS) => (
  record
  && typeof record.requestId === 'string'
  && record.requestId.length > 0
  && record.requestId.length <= 64
  && /^[A-Za-z0-9._:-]+$/.test(record.requestId)
  && Number.isFinite(Number(record.createdAt))
  && now - Number(record.createdAt) >= 0
  && now - Number(record.createdAt) < ttlMs
);

const getSubmissionStorageKey = (coordinator, fingerprint) => `${coordinator.cachePrefix}${fingerprint}`;

const readCachedSubmissionRequestId = (coordinator, fingerprint, now = Date.now()) => {
  const volatileRecord = coordinator.requestIds.get(fingerprint);
  if (isUsableCachedRequestId(volatileRecord, now, coordinator.ttlMs)) return volatileRecord;
  coordinator.requestIds.delete(fingerprint);

  const storage = getSessionStorage();
  if (!storage) return null;
  const storageKey = getSubmissionStorageKey(coordinator, fingerprint);
  try {
    const storedRecord = JSON.parse(storage.getItem(storageKey) || 'null');
    if (isUsableCachedRequestId(storedRecord, now, coordinator.ttlMs)) {
      coordinator.requestIds.set(fingerprint, storedRecord);
      return storedRecord;
    }
    storage.removeItem(storageKey);
  } catch {
    try {
      storage.removeItem(storageKey);
    } catch {
      // sessionStorage 不可用时只关闭跨刷新复用，当前页面单飞不受影响。
    }
  }
  return null;
};

const cacheSubmissionRequestId = (coordinator, fingerprint, requestId, createdAt = Date.now()) => {
  const record = { requestId, createdAt };
  coordinator.requestIds.set(fingerprint, record);

  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(getSubmissionStorageKey(coordinator, fingerprint), JSON.stringify(record));
  } catch {
    // 内存缓存已经写入，不让存储配额或隐私模式阻断正常提交。
  }
};

const clearCachedSubmissionRequestId = (coordinator, fingerprint, requestId) => {
  const volatileRecord = coordinator.requestIds.get(fingerprint);
  if (!requestId || volatileRecord?.requestId === requestId) {
    coordinator.requestIds.delete(fingerprint);
  }

  const storage = getSessionStorage();
  if (!storage) return;
  const storageKey = getSubmissionStorageKey(coordinator, fingerprint);
  try {
    const storedRecord = JSON.parse(storage.getItem(storageKey) || 'null');
    if (!requestId || storedRecord?.requestId === requestId || !isUsableCachedRequestId(storedRecord, Date.now(), coordinator.ttlMs)) {
      storage.removeItem(storageKey);
    }
  } catch {
    try {
      storage.removeItem(storageKey);
    } catch {
      // 清理失败最多保留到 TTL；读取时仍会按时间拒绝复用。
    }
  }
};

const makeUnknownSubmissionOutcomeError = (message) => {
  const error = new Error(message);
  error.videoSubmissionOutcomeUnknown = true;
  return error;
};

const getSubmissionErrorText = (error) => [
  error?.message,
  error?.code,
  error?.data?.message,
  error?.data?.status,
  error?.data?.error,
  error?.data?.data?.message,
  error?.data?.data?.status,
  error?.data?.data?.error,
]
  .filter(value => value != null && String(value).trim())
  .map(value => String(value).trim().toLowerCase())
  .join(' ');

const isUnknownSubmissionOutcomeError = (error) => {
  if (error?.videoSubmissionOutcomeUnknown === true) return true;
  const errorText = getSubmissionErrorText(error);
  return /submission[_\s-]*unknown|unknown\s+(?:submission|submit(?:ted)?)\s+outcome|提交(?:结果)?未知|结果未知|禁止(?:自动)?(?:重提|重试)|不得(?:自动)?(?:重提|重试)|待(?:人工)?对账|视频任务已提交至厂商，但主账落库失败/.test(errorText);
};

const isAcceptedButUnpersistedSubmissionError = (error) => (
  /视频任务已提交至厂商，但主账落库失败/.test(getSubmissionErrorText(error))
);

const isExplicitBackendSubmissionUnknownError = (error) => {
  const errorText = getSubmissionErrorText(error);
  return !isAcceptedButUnpersistedSubmissionError(error)
    && /submission[_\s-]*unknown|提交(?:结果)?未知|结果未知|禁止(?:自动)?(?:重提|重试)|待(?:人工)?对账/.test(errorText);
};

const isSafeFreshRequestRolloverError = (error) => {
  const detail = error?.data?.data;
  if (detail?.status === 'submission_unknown'
    && detail?.retryAction === 'new_request_id'
    && detail?.safeToStartFreshRequest === true) {
    return true;
  }
  // Rolling-upgrade compatibility: this exact legacy message is emitted only when the current
  // request was stopped at an existing local reservation, before any provider create call.
  const errorText = getSubmissionErrorText(error);
  return /相同视频的上次提交结果未知/.test(errorText)
    && /本次不会自动重放/.test(errorText)
    && /新请求提交/.test(errorText);
};

const isAmbiguousSubmissionError = (error) => {
  // 后端可能用明确的业务 code 返回“提交结果未知”。先把它识别为歧义语义；
  // 是否允许释放 requestId，由具体厂商协调器在 catch 中决定。
  if (isUnknownSubmissionOutcomeError(error)) return true;

  const httpStatus = Number(error?.status ?? error?.statusCode);
  if (Number.isFinite(httpStatus)) {
    if (httpStatus >= 400 && httpStatus < 500) return false;
    if (httpStatus >= 500) return true;
  }

  const businessCode = Number(error?.code);
  if (Number.isFinite(businessCode)) {
    // ApiResult 的普通业务错误说明后端已给出明确结论，即使 code=500 也不能
    // 继续占用旧 requestId；只有上面的 submission_unknown 语义是例外。
    return false;
  }

  const name = String(error?.name || '').toLowerCase();
  if (['aborterror', 'timeouterror', 'typeerror'].includes(name)) return true;

  const message = `${getSubmissionErrorText(error)} ${String(error || '')}`.trim().toLowerCase();
  if (/请求超时|网络(?:连接)?(?:异常|错误|中断|失败)|连接(?:被)?(?:重置|中断|关闭)|connection\s*(?:was\s*)?(?:reset|closed|aborted)|premature(?:ly)?\s*closed|socket\s*hang\s*up|failed\s*to\s*fetch|fetch\s*failed|network\s*(?:error|request\s*failed)|load\s*failed|econnreset|etimedout|eai_again/.test(message)) {
    return true;
  }

  // 其余带业务 code 的错误视为服务端已明确拒绝；无 code 的普通前端错误也
  // 不应无限占用一个幂等键。
  if (error?.code != null) return false;
  return false;
};

const performVideoTaskSubmission = async ({
  headers,
  normalizedPayload,
  requestedProvider,
  endpoint = '/api/video/generate',
}) => {
  const requestBody = { ...normalizedPayload };
  delete requestBody.shotId;

  const response = await authenticatedFetch(`${API_HOST}${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
  });

  if (response && typeof response === 'object') {
    // ✅ 响应标准化 (Backend -> Frontend)
    // 优先取 task_id (后端字段), 其次 taskId (兼容), 最后 id
    const rawId = response.task_id || response.taskId || response.id;

    if (!rawId) {
      console.error('[VideoService] No taskId in response', {
        provider: requestedProvider,
        responseKeys: Object.keys(response),
      });
      throw makeUnknownSubmissionOutcomeError('后端未返回任务ID，请检查后端日志');
    }

    return {
      taskId: rawId,
      internalTaskId: response.internalTaskId || response.internal_task_id || null,
      provider: response.provider || normalizedPayload.provider,
      status: 'processing',
    };
  }
  throw makeUnknownSubmissionOutcomeError('Invalid response format');
};

/**
 * 提交视频生成任务（通用接口）
 * @param {Object} payload - 任务参数
 * @returns {Promise<Object>} - 返回包含 taskId 的对象
 */
export const submitVideoTask = (payload) => {
  const rawRequestedProvider = payload.provider || payload.videoModel || 'minimax';
  if (String(rawRequestedProvider).trim().toLowerCase() === 'wand_vega') {
    throw new Error('Wand Vega 必须使用专用提交入口，以保留请求 ID 并避免重复付费');
  }
  assertSeedance25SubmissionProvider(rawRequestedProvider);
  const seedance25Provider = normalizeSeedance25SubmissionProvider(rawRequestedProvider);
  const requestedProvider = seedance25Provider || rawRequestedProvider;
  const sourcePayload = isDomesticOfficialSeedance(requestedProvider)
    ? stripSeedance25AssetIds(normalizeSeedance25SourceMedia(requestedProvider, validateDomesticSeedanceRequest(payload)))
    : getSeedance25Capabilities(requestedProvider)
    ? stripSeedance25AssetIds(normalizeSeedance25SourceMedia(requestedProvider, payload))
    : payload;

  // ✅ 1. 参数标准化 (Frontend -> Backend DTO)
  const normalizedPayloadBase = {
    ...sourcePayload,
    // 如果前端传了 imageUrl 但没传 startImageUrl，自动映射
    startImageUrl: sourcePayload.startImageUrl || sourcePayload.imageUrl || sourcePayload.generated_image,
    // 确保 provider 存在
    provider: requestedProvider,
  };
  const seedance25Capabilities = getSeedance25Capabilities(requestedProvider);
  if (isModelArkSeedance25Provider(requestedProvider)) {
    normalizedPayloadBase.model = seedance25Capabilities.model;
    normalizedPayloadBase.resolution = normalizeSeedance25Resolution(requestedProvider, sourcePayload.resolution);
  }
  if (seedance25Capabilities?.frameRatio) {
    // Apply only a provider's explicitly declared frame-mode constraint.
    const ratio = normalizeSeedance25RatioForMode(
      requestedProvider,
      sourcePayload.generationMode || sourcePayload.mode,
      sourcePayload.aspectRatio || sourcePayload.ratio,
    );
    normalizedPayloadBase.ratio = ratio;
    normalizedPayloadBase.aspectRatio = ratio;
  }
  if (isWanxSubmissionProvider(requestedProvider)) {
    // Wan3 的 AI 水印默认关闭。这里在统一提交边界显式补 false，避免新入口
    // 漏传字段时只能依赖后端兜底；用户明确打开开关时仍保留 true。
    normalizedPayloadBase.watermark = sourcePayload.watermark != null
      ? sourcePayload.watermark === true
      : sourcePayload.watermarkEnabled === true;
  }
  const callerRequestId = sourcePayload.requestId || '';
  // 前端业务作用域用于隔离“内容相同但生成意图不同”的载体（例如两个画布节点）。
  // Wan3 只将业务作用域用于本地指纹，绝不进入厂商 payload。
  const clientSubmissionScope = sourcePayload.clientSubmissionScope || '';
  delete normalizedPayloadBase.requestId;
  delete normalizedPayloadBase.clientSubmissionScope;

  const headers = { 'Content-Type': 'application/json' };
  if (sourcePayload.shotId != null) {
    headers['X-Shot-Id'] = String(sourcePayload.shotId);
  }

  const submissionCoordinator = getSubmissionCoordinator(requestedProvider);
  if (!submissionCoordinator) {
    return performVideoTaskSubmission({
      headers,
      normalizedPayload: {
        ...normalizedPayloadBase,
        requestId: callerRequestId || createVideoRequestId(),
      },
      requestedProvider,
    });
  }

  const requestBodyForFingerprint = { ...normalizedPayloadBase };
  delete requestBodyForFingerprint.shotId;
  const serializedRequest = stableJsonStringify({
    request: requestBodyForFingerprint,
    shotId: sourcePayload.shotId == null ? null : String(sourcePayload.shotId),
    clientSubmissionScope: String(clientSubmissionScope),
    user: getVideoSubmissionUserScope(),
  });
  const inFlightFingerprint = shortVolatileFingerprint(
    `${serializedRequest}\ncallerRequestId=${callerRequestId}`,
  );
  const existingSubmission = submissionCoordinator.inFlight.get(inFlightFingerprint);
  if (existingSubmission) return existingSubmission;

  let submissionPromise;
  submissionPromise = (async () => {
    const persistentFingerprint = await createPersistentFingerprint(serializedRequest);
    const cachedRecord = callerRequestId
      ? null
      : readCachedSubmissionRequestId(submissionCoordinator, persistentFingerprint);
    let requestId = callerRequestId || cachedRecord?.requestId || createVideoRequestId();
    if (!callerRequestId && !cachedRecord) {
      cacheSubmissionRequestId(submissionCoordinator, persistentFingerprint, requestId);
    }

    try {
      let usedSafeFreshRequestRollover = false;
      while (true) {
        try {
          const result = await performVideoTaskSubmission({
            headers,
            normalizedPayload: {
              ...normalizedPayloadBase,
              requestId,
              ...(clientSubmissionScope && submissionCoordinator.forwardClientSubmissionScope ? {
                clientSubmissionScope: String(clientSubmissionScope),
              } : {}),
            },
            requestedProvider,
          });
          if (!callerRequestId) {
            clearCachedSubmissionRequestId(submissionCoordinator, persistentFingerprint, requestId);
          }
          return result;
        } catch (error) {
          const safeFreshRequestRollover = Boolean(
            !callerRequestId
            && cachedRecord
            && !usedSafeFreshRequestRollover
            && submissionCoordinator.retryKnownPreviousUnknownWithFreshRequest
            && isSafeFreshRequestRolloverError(error),
          );
          if (safeFreshRequestRollover) {
            clearCachedSubmissionRequestId(submissionCoordinator, persistentFingerprint, requestId);
            requestId = createVideoRequestId();
            cacheSubmissionRequestId(
              submissionCoordinator,
              persistentFingerprint,
              requestId,
            );
            usedSafeFreshRequestRollover = true;
            continue;
          }

          const releaseConfirmedWanxUnknown = Boolean(
            submissionCoordinator.releaseRequestIdOnConfirmedUnknown
            && isExplicitBackendSubmissionUnknownError(error),
          );
          if (!callerRequestId && (
            !isAmbiguousSubmissionError(error)
            || releaseConfirmedWanxUnknown
          )) {
            clearCachedSubmissionRequestId(submissionCoordinator, persistentFingerprint, requestId);
          }
          throw error;
        }
      }
    } finally {
      if (submissionCoordinator.inFlight.get(inFlightFingerprint) === submissionPromise) {
        submissionCoordinator.inFlight.delete(inFlightFingerprint);
      }
    }
  })();

  submissionCoordinator.inFlight.set(inFlightFingerprint, submissionPromise);
  return submissionPromise;
};

const submitWandTask = (payload, endpoint) => {
  if (payload?.provider !== 'wand_vega') {
    throw new Error('Wand Vega 视频任务的服务商配置无效');
  }
  const sourcePayload = { ...payload };
  const callerRequestId = sourcePayload.requestId || '';
  const clientSubmissionScope = sourcePayload.clientSubmissionScope || '';
  delete sourcePayload.requestId;
  delete sourcePayload.clientSubmissionScope;
  const serializedRequest = stableJsonStringify({
    endpoint,
    request: sourcePayload,
    clientSubmissionScope: String(clientSubmissionScope),
    user: getVideoSubmissionUserScope(),
  });
  const coordinator = WAND_SUBMISSION_COORDINATOR;
  const inFlightFingerprint = shortVolatileFingerprint(`${serializedRequest}\ncallerRequestId=${callerRequestId}`);
  const existingSubmission = coordinator.inFlight.get(inFlightFingerprint);
  if (existingSubmission) return existingSubmission;

  let submissionPromise;
  submissionPromise = (async () => {
    const persistentFingerprint = await createPersistentFingerprint(serializedRequest);
    const cachedRecord = callerRequestId ? null : readCachedSubmissionRequestId(coordinator, persistentFingerprint);
    const requestId = callerRequestId || cachedRecord?.requestId || createVideoRequestId();
    if (!callerRequestId && !cachedRecord) cacheSubmissionRequestId(coordinator, persistentFingerprint, requestId);
    try {
      const result = await performVideoTaskSubmission({
        headers: { 'Content-Type': 'application/json' },
        normalizedPayload: { ...sourcePayload, requestId },
        requestedProvider: 'wand_vega',
        endpoint,
      });
      if (!callerRequestId) clearCachedSubmissionRequestId(coordinator, persistentFingerprint, requestId);
      return result;
    } catch (error) {
      const outcomeAmbiguous = isAmbiguousSubmissionError(error);
      if (!callerRequestId && !outcomeAmbiguous) {
        clearCachedSubmissionRequestId(coordinator, persistentFingerprint, requestId);
      }
      if (outcomeAmbiguous) {
        error.message = `${error.message || '请求结果未知'}；请先检查任务记录，不要以新请求 ID 重复提交`;
      }
      throw error;
    } finally {
      if (coordinator.inFlight.get(inFlightFingerprint) === submissionPromise) {
        coordinator.inFlight.delete(inFlightFingerprint);
      }
    }
  })();
  coordinator.inFlight.set(inFlightFingerprint, submissionPromise);
  return submissionPromise;
};

/** VideoAgent's dedicated Vega carrier. Never use the generic submitVideoTask path. */
export const submitWandVideoAgentTask = (payload) => {
  if (payload?.source === 'episode_mini_storyboard_video' || payload?.miniStoryboardSegmentId != null) {
    throw new Error('Wand Vega 小故事板任务不能通过 VideoAgent 入口提交');
  }
  return submitWandTask(payload, '/api/video/agent/generate');
};

/** Mini-storyboard Vega carrier; same durable request-ID guard, separate backend route. */
export const submitWandStoryboardTask = (payload) => {
  const requiredCoordinates = [payload?.projectId, payload?.episodeId, payload?.miniStoryboardSegmentId, payload?.shotNumber];
  if (payload?.source !== 'episode_mini_storyboard_video'
    || requiredCoordinates.some(value => !Number.isInteger(Number(value)) || Number(value) < 1)) {
    throw new Error('Wand Vega 小故事板任务缺少有效的项目、剧集、片段或镜头编号');
  }
  return submitWandTask(payload, '/api/video/generate');
};

/**
 * 查询视频任务状态（通用接口）
 * @param {string} provider - 提供商名称
 * @param {string} taskId - 任务ID
 * @param {Object} extra - 额外参数（projectId, prompt, model）
 * @returns {Promise<Object>} - 任务状态
 */
/**
 * 查询视频任务状态
 * @param {string} provider - 提供商名称
 * @param {string} taskId - 任务ID
 * @param {Object} extra - 额外参数（projectId, episodeId, prompt, model, subModel, duration, mode, sound, resolution, hasReferenceVideo）
 * @returns {Promise<Object>} - 任务状态
 */
export const queryVideoStatus = async (provider, taskId, extra = {}) => {
  if (getSeedance25Capabilities(provider)?.retired) {
    return { taskId, provider, status: 'failed', errorMessage: '历史 LAS 线路已停用，不能继续查询；重新生成请使用海外 Dreamina 代理。' };
  }
  const params = new URLSearchParams({ provider, taskId });
  if (extra.projectId) params.set('projectId', extra.projectId);
  if (extra.episodeId) params.set('episodeId', extra.episodeId);
  if (extra.storyboardBoardIndex) params.set('storyboardBoardIndex', extra.storyboardBoardIndex);
  if (extra.model) params.set('model', extra.model);
  if (extra.subModel) params.set('subModel', extra.subModel);
  if (extra.duration != null) params.set('duration', extra.duration);
  if (extra.mode) params.set('mode', extra.mode);
  if (extra.sound) params.set('sound', extra.sound);
  if (extra.resolution) params.set('resolution', extra.resolution);
  if (extra.hasReferenceVideo != null) params.set('hasReferenceVideo', String(!!extra.hasReferenceVideo));
  const url = `${API_HOST}/api/video/status?${params.toString()}`;
  const response = await authenticatedFetch(url);
  return response;
};

// ============================================================
// Vidu 特有接口
// ============================================================

/**
 * Vidu: 视频延长
 * @param {string} videoUrl - 原视频URL
 * @param {number} extendDuration - 延长时长（4或8秒）
 * @param {string} prompt - 提示词
 * @param {string} resolution - 分辨率
 * @returns {Promise<Object>}
 */
export const extendViduVideo = async (videoUrl, extendDuration, prompt, resolution, options = {}) => {
  const response = await authenticatedFetch(`${API_HOST}/api/vidu/extend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      videoUrl,
      extendDuration,
      prompt,
      resolution,
      ...options,
    }),
  });
  return response;
};

/**
 * Vidu: 取消任务
 * @param {string} taskId - 任务ID
 * @returns {Promise<Object>}
 */
export const cancelViduTask = async (taskId) => {
  const response = await authenticatedFetch(`${API_HOST}/api/vidu/task/cancel?taskId=${taskId}`, {
    method: 'POST',
  });
  return response;
};

/**
 * Vidu: 环绕视频
 * @param {string} imageUrl - 图片URL
 * @returns {Promise<Object>}
 */
export const generateOrbitVideo = async (imageUrl, options = {}) => {
  const response = await authenticatedFetch(`${API_HOST}/api/video/generate-orbit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageUrl, ...options }),
  });
  return response;
};

/**
 * MiniMax: 主体参考视频生成
 * @param {Object} params - 参数
 * @returns {Promise<Object>}
 */
export const generateSubjectReference = async (params) => {
  const response = await authenticatedFetch(`${API_HOST}/api/minimax/subject-reference`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return response;
};

// ============================================================
// MiniMax 特有接口
// ============================================================

/**
 * MiniMax: 视频延长
 * @param {string} videoUrl - 原视频URL
 * @param {number} extendDuration - 延长时长（4或8秒）
 * @param {string} prompt - 提示词
 * @returns {Promise<Object>}
 */
export const extendMinimaxVideo = async (videoUrl, extendDuration, prompt, options = {}) => {
  const response = await authenticatedFetch(`${API_HOST}/api/minimax/extend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      videoUrl,
      extendDuration,
      prompt,
      ...options
    }),
  });
  return response;
};

/**
 * MiniMax: 取消任务
 * @param {string} taskId - 任务ID
 * @returns {Promise<Object>}
 */
export const cancelMinimaxTask = async (taskId) => {
  const response = await authenticatedFetch(`${API_HOST}/api/minimax/task/cancel?taskId=${taskId}`, {
    method: 'POST',
  });
  return response;
};

/**
 * 万相3.0：上游仅允许取消仍处于 PENDING 的任务。
 */
export const cancelWanxTask = async (taskId) => {
  return authenticatedFetch(`${API_HOST}/api/wanx/task/${encodeURIComponent(taskId)}/cancel`, {
    method: 'POST',
  });
};

// ============================================================
// 通用工具函数
// ============================================================

/**
 * 根据提供商取消任务
 * @param {string} provider - 提供商名称
 * @param {string} taskId - 任务ID
 * @returns {Promise<Object>}
 */
export const cancelTask = async (provider, taskId) => {
  switch (provider) {
    case 'vidu':
      return cancelViduTask(taskId);
    case 'minimax':
    case 'minimaxService':
      return cancelMinimaxTask(taskId);
    case 'wanx':
      return cancelWanxTask(taskId);
    default:
      throw new Error(`Cancel not supported for provider: ${provider}`);
  }
};

/**
 * 根据提供商延长视频
 * @param {string} provider - 提供商名称
 * @param {Object} params - 参数
 * @returns {Promise<Object>}
 */
export const extendVideo = async (provider, params) => {
  switch (provider) {
    case 'vidu':
      return extendViduVideo(params.videoUrl, params.extendDuration, params.prompt, params.resolution, params);
    case 'minimax':
    case 'minimaxService':
      return extendMinimaxVideo(params.videoUrl, params.extendDuration, params.prompt, params);
    default:
      throw new Error(`Extend not supported for provider: ${provider}`);
  }
};

// ============================================================
// 轮询任务状态
// ============================================================

/**
 * 开始轮询任务状态
 * @param {string} provider - 提供商名称
 * @param {string} taskId - 任务ID
 * @param {Object} options - 配置选项
 * @param {Function} options.onSuccess - 成功回调
 * @param {Function} options.onError - 错误回调
 * @param {Function} options.onProgress - 进度回调
 * @param {number} options.interval - 轮询间隔（毫秒）
 * @returns {Function} - 停止轮询的函数
 */
export const startPolling = (provider, taskId, options = {}) => {
  const {
    onSuccess,
    onError,
    onProgress,
    interval: requestedInterval,
    projectId,
    episodeId,
    prompt,
    model,
    subModel,
    duration,
    mode,
    sound,
    resolution,
    hasReferenceVideo,
    storyboardBoardIndex,
  } = options;
  const validRequestedInterval = Number.isFinite(Number(requestedInterval)) && Number(requestedInterval) > 0
    ? Number(requestedInterval)
    : null;
  const usesDurablePolling = isDurableVideoPollingProvider(provider);
  const interval = usesDurablePolling
    ? Math.max(15000, validRequestedInterval || 15000)
    : (validRequestedInterval || 3000);
  const startedAt = Date.now();
  const maxPollingMs = VIDEO_POLLING_MAX_MS;

  let isRunning = true;
  let timeoutId = null;
  let errorCount = 0;

  const poll = async () => {
    if (!isRunning) return;
    if (Date.now() - startedAt >= maxPollingMs) {
      isRunning = false;
      if (onError) onError('视频任务查询已达到120分钟，请稍后从任务记录继续查看结果');
      return;
    }

    try {
      let data;

      // 统一走 queryVideoStatus（后端会自动存资源库 + 费用统计）
      data = await queryVideoStatus(provider, taskId, {
        projectId, episodeId, prompt, model,
        subModel, duration, mode, sound, resolution, hasReferenceVideo, storyboardBoardIndex,
      });
      errorCount = 0;

      const rawStatus = data.status || data.state || data.taskStatus || data.task_status || data.task_status_msg;
      const status = String(rawStatus || '').toLowerCase();
      const resultVideoUrl = data.videoUrl || data.result?.videoUrl || data.result?.video_url || data.output?.videoUrl || data.output?.video_url || data.url;
      const finalStatuses = ['success', 'completed', 'done', 'succeed', 'succeeded', 'finished'];
      const isFinalSuccessStatus = finalStatuses.includes(status);
      const isSuccess = isFinalSuccessStatus && !!resultVideoUrl;
      const isFinishedWithoutUrl = isFinalSuccessStatus && !resultVideoUrl;
      const isFailed = ['failed', 'error', 'fail', 'canceled', 'cancelled', 'timeout', 'expired'].includes(status);

      if (isSuccess) {
        isRunning = false;
        if (onSuccess) {
          onSuccess({
            ...data,
            videoUrl: resultVideoUrl,
            thumbnail: data.thumbnail || data.result?.thumbnail,
            duration: data.duration || data.result?.duration,
            cost: data.cost || data.result?.cost,
            credits: data.credits || data.result?.credits
          });
        }
      } else if (isFinishedWithoutUrl) {
        isRunning = false;
        if (onError) {
          onError('视频任务已完成，但服务商没有返回可播放的视频地址');
        }
      } else if (isFailed) {
        isRunning = false;
        if (onError) {
          onError(data.errorMessage || data.message || data.error?.message || data.task_status_msg || '生成失败');
        }
      } else {
        if (onProgress) {
          onProgress({
            status: status === 'queued' ? 'queued' : 'processing',
            progress: Math.min(((Date.now() / 1000) % 100), 95) // 模拟进度
          });
        }
        timeoutId = setTimeout(poll, interval);
      }
    } catch (error) {
      console.error('Polling error:', error);
      errorCount += 1;
      // 异步长视频任务可能已经生成并计费，短暂网络故障不能让页面放弃结果；最多跟随后端等120分钟。
      if (usesDurablePolling && Date.now() - startedAt < maxPollingMs) {
        timeoutId = setTimeout(poll, interval);
      } else if (errorCount >= 5) {
        isRunning = false;
        if (onError) {
          onError('网络连接中断或服务异常，已停止轮询');
        }
      } else {
        timeoutId = setTimeout(poll, interval);
      }
    }
  };

  if (usesDurablePolling) timeoutId = setTimeout(poll, interval);
  else poll();

  // 返回停止轮询的函数
  return () => {
    isRunning = false;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
};

// ============================================================
// 后处理工具 (Post-Processing)
// ============================================================

/**
 * 去字幕 - 提交任务
 * @param {Object} params - 参数
 * @param {string} params.videoUrl - 视频 URL (仅支持方舟原始 URL)
 * @param {number} [params.shotId] - 分镜 ID（可选，用于后端兜底落库）
 * @returns {Promise<Object>} - { taskId, status }
 */
export const submitEraseSubtitle = async ({ videoUrl, shotId }) => {
  const body = { videoUrl };
  if (shotId != null) body.shotId = shotId;
  const response = await authenticatedFetch(`${API_HOST}/api/video/erase-subtitle`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const taskId = response.task_id || response.taskId || response.id;
  if (!taskId) throw new Error('未返回任务ID');

  return { taskId, status: 'processing' };
};

/**
 * 后处理任务状态轮询
 * @param {string} taskType - 任务类型 ('erase-subtitle')
 * @param {string} taskId - 任务ID
 * @param {Object} options - 配置选项
 * @param {Function} options.onSuccess - 成功回调
 * @param {Function} options.onError - 错误回调
 * @param {Function} options.onProgress - 进度回调
 * @param {number} options.interval - 轮询间隔（毫秒，默认 5000）
 * @param {number} [options.shotId] - 分镜 ID（可选，透传给后端用于兜底落库）
 * @param {number} [options.projectId] - 项目 ID
 * @param {number} [options.episodeId] - 集数 ID
 * @returns {Function} - 停止轮询的函数
 */
export const startPostProcessPolling = (taskType, taskId, options = {}) => {
  if (taskType !== 'erase-subtitle') {
    options.onError?.('该视频后处理功能已下线');
    return () => {};
  }

  const {
    onSuccess,
    onError,
    onProgress,
    interval = 5000,
    shotId,
    projectId,
    episodeId,
  } = options;

  let isRunning = true;
  let timeoutId = null;
  let errorCount = 0;

  const poll = async () => {
    if (!isRunning) return;

    try {
      const qs = new URLSearchParams({ type: taskType, taskId });
      if (shotId != null) qs.set('shotId', String(shotId));
      if (projectId != null) qs.set('projectId', String(projectId));
      if (episodeId != null) qs.set('episodeId', String(episodeId));
      const data = await authenticatedFetch(
        `${API_HOST}/api/video/post-process/status?${qs.toString()}`
      );

      const rawStatus = data.status || data.state || data.taskStatus || data.task_status || data.task_status_msg;
      const status = String(rawStatus || '').toLowerCase();
      const resultVideoUrl = data.videoUrl || data.result?.video_url || data.result?.videoUrl;
      const isSuccess = ['success', 'completed', 'done', 'succeed', 'succeeded', 'finished'].includes(status) || !!resultVideoUrl;
      const isFailed = ['failed', 'error', 'fail', 'canceled', 'cancelled', 'timeout', 'expired'].includes(status);

      if (isSuccess) {
        isRunning = false;
        onSuccess?.({
          videoUrl: resultVideoUrl,
          duration: data.duration || data.result?.duration,
          ...data
        });
      } else if (isFailed) {
        isRunning = false;
        onError?.(data.errorMessage || data.message || data.error?.message || '处理失败');
      } else {
        onProgress?.({ status: status === 'queued' ? 'queued' : 'processing' });
        timeoutId = setTimeout(poll, interval);
      }
    } catch (error) {
      console.error('[PostProcess Polling] Error:', error);
      errorCount++;

      if (errorCount >= 5) {
        isRunning = false;
        onError?.('网络异常，已停止轮询');
      } else {
        timeoutId = setTimeout(poll, interval);
      }
    }
  };

  poll();

  return () => {
    isRunning = false;
    if (timeoutId) clearTimeout(timeoutId);
  };
};
